import { defineComponent as _defineComponent } from 'vue'
import {
  toRef,
  inject,
  markRaw,
  onBeforeUnmount,
  ref,
  watch,
  useSlots,
  useListeners,
  computed,
} from 'vue'
import equal from 'fast-deep-equal'
import { apiSymbol, mapSymbol } from '@/components/core/map/utils'

type MapMarkerEvents = {
  (e: 'click', event: google.maps.MapMouseEvent): void
  (e: 'drag', event: google.maps.MapMouseEvent): void
  (e: 'dragend', event: google.maps.MapMouseEvent): void
  (e: 'dragstart'): void
  (e: 'gmp-click'): google.maps.marker.AdvancedMarkerClickEvent // only available with v=beta
}

export interface MapMarkerProps {
  options: google.maps.marker.AdvancedMarkerElementOptions
  hide?: boolean
  pinOptions?: google.maps.marker.PinElementOptions
}

export default /*#__PURE__*/_defineComponent({
  __name: 'MapMarker',
  props: {
    options: null,
    hide: { type: Boolean },
    pinOptions: null
  },
  emits: ["click", "drag", "dragend", "dragstart", "gmp-click"],
  setup(__props: any, { expose }: { emit: ({
  (e: 'click', event: google.maps.MapMouseEvent): void
  (e: 'drag', event: google.maps.MapMouseEvent): void
  (e: 'dragend', event: google.maps.MapMouseEvent): void
  (e: 'dragstart'): void
  (e: 'gmp-click'): google.maps.marker.AdvancedMarkerClickEvent // only available with v=beta
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  options: google.maps.marker.AdvancedMarkerElementOptions
  hide?: boolean
  pinOptions?: google.maps.marker.PinElementOptions
};




const map = inject(mapSymbol, ref())
const api = inject(apiSymbol, ref())

const slots = useSlots()
const listeners = useListeners()

const options = toRef(props, 'options')
const pinOptions = toRef(props, 'pinOptions')
const markerContentRef = ref<HTMLDivElement | null>(null)
const marker = ref<google.maps.marker.AdvancedMarkerElement>()
const hasSlotContent = computed(() =>
  slots.default?.().some((vnode) => !vnode.isComment),
)

watch(
  () => props.hide,
  (hide) => {
    if (marker.value) {
      marker.value.map = hide ? null : map.value
    }
  },
)

watch(
  [map, options, pinOptions],
  async (_, [oldMap, oldOptions, oldPinOptions]) => {
    const hasOptionChange =
      !equal(options.value, oldOptions) ||
      !equal(pinOptions.value, oldPinOptions)
    const hasChanged = hasOptionChange || map.value !== oldMap

    if (!map.value || !hasChanged) return

    const { AdvancedMarkerElement, PinElement } =
      (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary

    if (marker.value) {
      const { content, ...otherOptions } = options.value
      Object.assign(marker.value, {
        content: pinOptions.value
          ? new PinElement(pinOptions.value).element
          : hasSlotContent.value
            ? markerContentRef.value
            : content,
        ...otherOptions,
      })
    } else {
      if (pinOptions.value) {
        options.value.content = new PinElement(pinOptions.value).element
      } else if (hasSlotContent.value) {
        options.value.content = markerContentRef.value
      }

      marker.value = markRaw(new AdvancedMarkerElement(options.value))

      if (!props.hide) {
        marker.value.map = map.value
      }

      Object.entries(listeners).forEach(([key, handler]) => {
        const handlers = Array.isArray(handler) ? handler : [handler]
        for (const handler of handlers) {
          marker.value?.addListener(key, handler)
        }
      })
    }
  },
  {
    immediate: true,
  },
)

onBeforeUnmount(() => {
  if (marker.value) {
    api.value?.event.clearInstanceListeners(marker.value)
    marker.value.map = null
  }
})

expose({ marker })

return { __sfc: true,props, map, api, slots, listeners, options, pinOptions, markerContentRef, marker, hasSlotContent }
}

})